import * as Sentry from "@sentry/react";

import { logDev, noop } from "..";

// Only the first argument becomes the Sentry issue; the rest land in `extra`. So
// callers pass the Error first and context second — a leading string would make the
// string itself the issue and bury the error.
export const forwardConsoleErrorToSentry = (
  error: string | Error,
  ...optionalParams: any[]
): void => {
  Sentry.captureException(error, {
    level: "error",
    extra: { "coned-rates": optionalParams },
  });
};

/**
 * Sentry tests each entry against the message, the exception value, and
 * "<type>: <value>". An entry that names an error type drops every error of
 * that type.
 *
 * A dropped event is never sent, so it appears nowhere in Sentry. An entry that
 * is too broad hides errors without leaving a trace.
 *
 * https://docs.sentry.io/platforms/javascript/configuration/filtering/
 * https://github.com/getsentry/sentry-javascript/issues/3440
 */
export const IGNORED_ERRORS = [
  "Non-Error promise rejection",

  // Connectivity loss during a request. An error from our own server arrives
  // with a status code instead, so it still reports.
  "Network Error",

  // Chrome and Safari word this differently, so match on the common prefix.
  "ResizeObserver loop",

  "webkit-masked-url",
  "<unknown>",
  "clone is not a function",

  // The WASM runtime logs each step of its retry as an error, so one failed
  // load reported four issues. The message it logs when it gives up is left out
  // on purpose, so a real failure still reports.
  "falling back to ArrayBuffer instantiation",
  "wasm streaming compile failed",
  "failed to asynchronously prepare wasm",
  "both async and sync fetching of the wasm failed",

  // The browser can reclaim a WebGL context at any time. The canvas then
  // recovers on its own.
  "GL Context was lost",

  // A cross-origin script threw and the browser withheld the details.
  "Script error.",

  // Avast extension error
  "_avast_submit",
];

export const SentryUtil = {
  init: () => {
    if (import.meta.env.MODE !== "production") {
      logDev("Skip initializing sentry.io in non-production environment");
      return;
    }

    globalThis.console.log = noop;
    globalThis.console.dir = noop;
    globalThis.console.table = noop;
    globalThis.console.warn = noop;
    globalThis.console.error = forwardConsoleErrorToSentry;

    // @ts-expect-error - Property 'content' does not exist on type 'Element'.
    const sentryDSN = document.querySelector("meta[name=SENTRY_DSN]")?.content;

    if (sentryDSN) {
      logDev("Initializing sentry.io error tracing");
      try {
        Sentry.init({
          dsn: sentryDSN,

          // Ties events to a deploy, so a stale-asset error can be told apart from
          // a live one. Falls back to undefined because Sentry handles a missing
          // release better than a blank one.
          release: __SENTRY_RELEASE__ || undefined,

          integrations: [],

          ignoreErrors: IGNORED_ERRORS,

          // Common things to ignore - https://gist.github.com/impressiver/5092952
          // https://docs.sentry.io/platforms/javascript/configuration/filtering/
          denyUrls: [
            // Facebook flakiness
            /graph\.facebook\.com/i,
            // Facebook blocked
            /connect\.facebook\.net\/en_US\/all\.js/i,
            // Woopra flakiness
            /eatdifferent\.com\.woopra-ns\.com/i,
            /static\.woopra\.com\/js\/woopra\.js/i,
            // Chrome extensions
            /extensions\//i,
            /^chrome:\/\//i,
            /^chrome-extension:\/\//i,
            // Other plugins
            /127\.0\.0\.1:4001\/isrunning/i, // Cacaoweb
            /webappstoolbarba\.texthelp\.com\//i,
            /metrics\.itunes\.apple\.com\.edgesuite\.net\//i,

            // add this to ignore safari webkit
            /.*@webkit-masked-url.*/,
          ],

          // We recommend adjusting this value in production, or using tracesSampler
          // for finer control
          tracesSampleRate: 0.1,
        });
      } catch (error) {
        logDev("Failed to initialize sentry.io");
        console.warn(error);
      }
    } else {
      logDev("Skip initializing sentry.io");
    }
  },
};
